import { Trip, Apartment, Traveller, BillData } from '@/types';
import { getApartmentPrice, getSkiPassPrice, getTransportationPrice, getMembershipFee } from './billCalculationService';

/**
 * Optimized version that calculates bill data for a single apartment
 * without processing all apartments and travellers
 */
export const getBillDataForApartment = (
  apartmentId: string,
  trip: Trip, 
  apartment: Apartment, 
  apartmentTravellers: Traveller[]
): BillData => {
  const accommodationCostPerPerson = getApartmentPrice(apartment, trip.apartmentTypes);

  const travellersData = apartmentTravellers.map(traveller => {
    const accommodationCost = accommodationCostPerPerson;
    const skiPassCost = getSkiPassPrice(traveller, trip.skiPasses, trip);
    const transportationCost = getTransportationPrice(traveller, trip.transportationOptions);
    const membershipFee = getMembershipFee(traveller, trip);

    return {
      name: traveller.name,
      accommodationCost,
      skiPassCost,
      transportationCost,
      membershipFee,
      total: accommodationCost + skiPassCost + transportationCost + membershipFee
    };
  });

  const totalAccommodation = travellersData.reduce((sum, t) => sum + t.accommodationCost, 0);
  const totalSkiPass = travellersData.reduce((sum, t) => sum + t.skiPassCost, 0);
  const totalTransportation = travellersData.reduce((sum, t) => sum + t.transportationCost, 0);
  const totalMembership = travellersData.reduce((sum, t) => sum + t.membershipFee, 0);

  return {
    apartmentId: apartment.id,
    apartmentName: apartment.name,
    travellers: travellersData,
    totalAccommodation,
    totalSkiPass,
    totalTransportation,
    totalMembership,
    grandTotal: totalAccommodation + totalSkiPass + totalTransportation + totalMembership,
    billStatus: apartment.billStatus || 'not_sent',
    isPaid: apartment.isPaid || false
  };
};

/**
 * Optimized version that gets only the specific apartment and its travellers
 * Returns null if apartment not found
 */
export const getSingleApartmentBillData = (
  apartmentId: string,
  trip: Trip,
  apartments: Apartment[],
  travellers: Traveller[]
): BillData | null => {
  const apartment = apartments.find(apt => apt.id === apartmentId);
  if (!apartment) return null;

  const apartmentTravellers = travellers.filter(t => t.apartmentId === apartmentId);
  return getBillDataForApartment(apartmentId, trip, apartment, apartmentTravellers);
};